/**
 * ReminderMailModal — Mahnung per E-Mail an den Kunden senden (mit PDF-Anhang).
 *
 * Erzeugt die nächste Mahnstufe und versendet sie als E-Mail. Der Begleittext
 * ist über das Briefsymbol (EmailTextEditor) editierbar; Beträge, neue Fälligkeit
 * und das Mahn-PDF werden serverseitig fix angehängt.
 *   POST /api/invoices/{id}/reminder  { send_email:true, custom_message, note }
 */
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, AlertTriangle } from "lucide-react";
import api, { formatPrice } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import EmailTextEditor from "../../components/dealer/EmailTextEditor";

export default function ReminderMailModal({ item, onClose, onSent }) {
    const [customMessage, setCustomMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const nextLevel = Math.min(3, (item?.reminder_level || 0) + 1);

    const send = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/invoices/${item.id}/reminder`, {
                send_email: true,
                custom_message: customMessage.trim() || undefined,
            });
            const info = r.data?.email;
            if (info && info.sent === false) {
                toast.warning(`Mahnstufe ${r.data?.reminder?.level} erstellt, aber Versand nicht möglich: ${info.reason || "unbekannt"}`);
            } else {
                toast.success(`Mahnung (Stufe ${r.data?.reminder?.level}) per E-Mail versendet${info?.mocked ? " (GEMOCKT — kein Resend-Key)" : ""}.`);
            }
            await onSent?.();
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Mahnung per E-Mail fehlgeschlagen.");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="reminder-mail-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-red-600" /> Mahnung per E-Mail</DialogTitle>
                    <DialogDescription>
                        Rechnung <strong>{item?.invoice_number}</strong> · {item?.customer_name} · offen <strong>{formatPrice(item?.open_amount || 0)}</strong>
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-sm text-xs text-red-900 flex gap-2">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>Es wird <strong>Mahnstufe {nextLevel}</strong> erstellt und als PDF an den Kunden gesendet.</span>
                    </div>
                    <EmailTextEditor
                        value={customMessage}
                        onChange={setCustomMessage}
                        label="E-Mail-Text ansehen / ändern"
                        immutableHint="Offener Betrag, Mahngebühr, neue Fälligkeit und das Mahn-PDF werden automatisch & unveränderbar angehängt."
                        testid="reminder-mail-text"
                    />
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" className="rounded-sm" onClick={onClose} data-testid="reminder-mail-cancel">Abbrechen</Button>
                    <Button type="button" onClick={send} disabled={busy} className="rounded-sm bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="reminder-mail-send">
                        {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Mahnung senden
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
